/**  
 *  Developers: Sakshi Bhise  
 *  File: AuthGate.tsx  
 *  Date: 06/18/2025  
 *  Project Name: AskHR  
 *  Team: DSET  
 *   
 *  Purpose:  
 *  - Implements the authentication logic for AskHR, including support for MSAL(Microsoft Authentication library) (web/desktop), Microsoft Teams, and auto-refresh.  
 *  - Handles token acquisition, session expiry, and error states.  
 *  - Provides global authentication context for all child components via React Context.  
 *  - Centralizes authentication state for scalability and consistency across the app.  
 */


// ======================= Import Statements ======================= //  
// React hooks for lifecycle, state, and references  
import React, { useEffect, useState, useRef } from "react";
// MSAL authentication hooks and types  
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./msalConfig";
// Microsoft Teams SDK for Teams authentication  
import * as teams from "@microsoft/teams-js";

// Custom authentication context and global app state provider  
import { AuthContext } from "./AuthContext";
import { AppStateProvider } from "./AppProvider";
// CSS module for loading and chat styles  
import styles from '../pages/chat/Chat.module.css';
// Fluent UI Spinner and Stack for loading feedback/layout  
import { Spinner, SpinnerSize, Stack } from "@fluentui/react";
// UI shown when authentication fails  
import AuthFailed from "../pages/AuthFailed";
// Event logging utility  
import { getAuthority, logEvent } from "../api";
import { Loader } from "@teva/ui";

// ======================= Spinner Styles ======================= //  
// Custom styles for loading spinner  
const spinnerStyles = {
  label: { fontSize: '16px' },                   // Readable label font size  
  circle: { width: 100, height: 100, borderWidth: 6 }, // Large, bold spinner  
};

// ======================= Device Detection ======================= //  
/**  
 * Checks if the app is running inside Microsoft Teams.  
 * Uses iframe detection, user agent, and Teams SDK with timeout.
 * Returns true if app is running on teams.
 */
async function isRunningInTeams() {
  const isInIframe = window.self !== window.top;
  // navigator.userAgent is read only property in JavaScript used to identify device properties. 
  const isTeamsUA = /teams/.test(navigator.userAgent.toLowerCase());

  if (!isInIframe) return false; // Not in iframe, not in Teams  

  if (typeof teams === "undefined") return isTeamsUA; // No Teams SDK, fallback to User Agent detection 

  // Try to initialize Teams SDK with a timeout of 1500ms 
  function initializeTeamsWithTimeout(ms = 1500) {
    return Promise.race([
      teams.app.initialize(), // function to initialize teams sdk 
      new Promise((_, reject) => setTimeout(() => reject(new Error("Teams init timeout")), ms))
    ]);
  }

  try {
    await initializeTeamsWithTimeout();
    const context = await teams.app.getContext();  // Retrieves the current context the frame is running in.
    return !!context;
  } catch {
    // Teams SDK failed or timed out, fallback to User Agent detection   
    return isTeamsUA;
  }
}

/**  
 * Detects if current device is a android tablet or iPad using user agent and touch points.
 * Returns false if device is not ipad or android tablet  
 */
function isTabletOrIpad() {
  // navigator.userAgent is read only property in JavaScript used to identify device properties.
  const ua = navigator.userAgent;
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return true; // i is Regular Expression variable whose name cannot be changed.
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true;
  // navigator.touchPoints  is a read only property in JavaScript that returns the max no. of simultaneous,multi-touch contacy points supported by the current device.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**  
 * Gets an authentication token from Microsoft Teams.  
 * Resolves with token or rejects with error string.
 * Returns token as a string eg ""  
 */
async function getTeamsToken(): Promise<string> {
  await teams.app.initialize();
  return new Promise((resolve, reject) => {
    teams.authentication.getAuthToken()
      .then(resolve)
      .catch(error => reject("Error getting token: " + error));
  });
}

// ======================= Main AuthGate Component ======================= //  
/**  
 * AuthGate  
 * Centralizes authentication and global state logic.  
 * - Attempts to acquire an MSAL token (web/desktop)  
 * - If running in Teams, acquires Teams token and exchanges for app token  
 * - Handles auto-refresh of tokens before expiry  
 * - Provides authentication context to child components  
 * - Shows loading spinner during authentication, or error on failure  
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // MSAL authentication context.
  // The useMsal() hook provides access to the MSAL (Microsoft Authentication Library) context.
  // - instance: The MSAL PublicClientApplication instance used to interact with Microsoft Identity Platform (e.g., login, acquire tokens).
  // - accounts: Array containing information about currently logged-in users. Each account object includes details like username, homeAccountId, etc.
  // - inProgress: Indicates the current MSAL authentication operation status (e.g., 'login', 'acquireToken', 'none').
  // - Eg: instance = { getActiveAccount(), acquireTokenSilent()}
  // - Eg: accounts = {id:"1234", username:"test@tevapharm.com"}
  // - Eg: inprogress= InteractionStatus.None

  const { instance, accounts, inProgress } = useMsal();
  // Authentication token state   
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expiresOn, setExpiresOn] = useState<Date | null>(null);
  const [isTeams, setIsTeams] = useState(false);

  // Ref to track and clean up token refresh timeouts  
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up refresh timer on component unmount  
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  // ======================= Main Authentication Effect ======================= //  
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return; // Wait for MSAL interactions to finish  

    //function to fetch the token using msal or teams
    const fetchAuthToken = async () => {
      setLoading(true);  // to show loading spinner in UI
      try {
        // --- Step 1: Try MSAL account authentication (silent) ---  
        const activeAccount = instance.getActiveAccount() || accounts[0];
        if (activeAccount) {
          try {
            const response = await instance.acquireTokenSilent({
              ...loginRequest,
              account: activeAccount,
            });  // if user have active account with valid token acquireTokenSilent will get the token
            setToken(response.accessToken);
            setExpiresOn(response.expiresOn as Date);
            setIsTeams(false);
            setLoading(false);
            //log frontend event
            logEvent(
              {
                "eventType": "Session Details",
                "browser": "App Running on Web Browser",
                "device": isTabletOrIpad() ? "Tablet/IPad" : "Laptop/Desktop",
                "screenWidth": window.innerWidth,
                "screenHeight": window.innerHeight
              },
              "",
              null,
              response.accessToken
            );
            return;
          } catch (error) {
            // Silent fail, try Teams or redirect next  
          }
        }

        // --- Step 2: If not authenticated, check for Teams environment ---  
        if (await isRunningInTeams()) {
          setIsTeams(true);
          try {
            const teamsToken = await getTeamsToken();
            // Exchange Teams token for app backend token  
            const response = await getAuthority(teamsToken)
            const data = await response.json();
            if (response.ok && data.access_token) {
              setToken(data.access_token);
              setExpiresOn(null); // Tokens from team doesn't have expiry details hence expires on is set to null
              setLoading(false);
              //log frontend event 
              logEvent(
                {
                  "eventType": "Session Details",
                  "browser": "App Running on Teams",
                  "device": isTabletOrIpad() ? "Tablet/IPad" : "Laptop/Desktop",
                  "screenWidth": window.innerWidth,
                  "screenHeight": window.innerHeight
                },
                "",
                null,
                data.access_token
              );
              return;
            }
          } catch (error) {
            setToken(null);
            setLoading(false);
            return;
          }
        } else {
          setIsTeams(false); // Not running in Teams  
        }

        // --- Step 3: Fallback to MSAL browser authentication ---  
        if (accounts.length > 0) {
          try {
            const response = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            setToken(response.accessToken);
            setExpiresOn(response.expiresOn as Date);
            setLoading(false);
            return;
          } catch (error) {
            // If there is an error while trying to acquire a token silently (e.g., user is not authenticated or token is expired),
            // prompt the user to login by redirecting them to the Microsoft login page.
            // This method will redirect the browser to the Microsoft Identity authentication page,
            // allowing the user to log in and grant permissions as specified in loginRequest.
            await instance.acquireTokenRedirect(loginRequest);
            // After calling acquireTokenRedirect, return to prevent further code execution.
            return;
          }
        } else {
          // If there is no account currently signed in (i.e., the user is not authenticated),
          // initiate the login process by redirecting the user to the Microsoft login page.
          // loginRedirect will also redirect the browser to Microsoft Identity authentication,
          // but is used specifically when no account is present in the session.
          await instance.loginRedirect(loginRequest);
          // After calling loginRedirect, return to prevent further code execution.
          return;

        }
      } catch (error) {
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthToken();

  }, [inProgress]);

  // ======================= Token Auto-Refresh Logic ======================= //  
  useEffect(() => {
    // Exit early if there's no token; nothing to refresh.
    if (!token) return;


    // If running inside Microsoft Teams, auto-refresh is not supported; exit early.  
    if (isTeams) return; // Can't auto-refresh Teams token  

    // Exit early if token expiry information is missing.  
    if (!expiresOn) return;

    // Calculate when to refresh: 2 minutes before token expires.  
    const now = new Date();
    const msUntilRefresh = expiresOn.getTime() - now.getTime() - 2 * 60 * 1000;

    // If the refresh time has passed or is right now, clear token and expiry.  
    if (msUntilRefresh <= 0) {
      setToken(null);          // Remove token from state  
      setExpiresOn(null);      // Remove expiry info from state  
      return;
    }

    // Set a timeout to refresh the token at the calculated time.  
    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        // Try to get the currently active account, or fallback to the first account in the list.  
        const account = instance.getActiveAccount() || accounts[0];
        if (account) {
          // Attempt to silently acquire a new token for the account, forcing a refresh.  
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
            forceRefresh: true,
          });
          setToken(response.accessToken); // Update state with new token  
          setExpiresOn(response.expiresOn as Date); // Update expiry info  
        } else {
          // No account found; clear token and expiry from state.  
          setToken(null);
          setExpiresOn(null);
        }
      } catch {
        // If an error occurs (e.g., silent token acquisition fails), clear token and expiry.  
        setToken(null);
        setExpiresOn(null);
      }
    }, msUntilRefresh);

    // Cleanup function: clears the timeout if dependencies change or component unmounts.  
    // Prevents multiple timeouts or memory leaks.  
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, [expiresOn, token, instance, accounts, isTeams]); // Re-run effect when these dependencies change

  // ======================= UI Rendering ======================= //  
  // --- Show loading spinner while authenticating ---  
  if (loading) {
    return (
      <div className={styles.container}>
        <Stack className={styles.chatEmptyState}>
           <Loader isLoading={true} loaderText='Authenticating...' imgWidth={400} isFullScreen={true} isOverlay={true} />
        </Stack>
      </div>
    );
  }

  // --- Show authentication failure UI if token missing ---  
  if (!token) {
    return <AuthFailed />;
  }

  // --- Provide global auth context and app state for all children components of the app ---
  return (
    // AuthContext.Provider makes authentication data (token and isAuthenticated) available to all descendant components.
    // Any component within this provider can access the authentication state using useContext(AuthContext).
    <AuthContext.Provider value={{ token, isAuthenticated: !!token }}>


      {/* AppStateProvider wraps the application and provides additional global app state management.  
        This allows child components to access and update shared app state as needed. */}
      <AppStateProvider>

        {/* Render all child components inside both providers.  
            Children will have access to authentication and app state contexts. */}
        {children}

      </AppStateProvider>
    </AuthContext.Provider>
  );
  // End of provider component. This pattern ensures global context is available throughout the app.
};
export default AuthGate;  